<template>
    <div class="login-container" v-if="!success">
        <div class="login-card">
            <!-- Header -->
            <div class="login-header">
                <div class="logo">
                    <div class="logo-icon">🏣</div>
                    <h1>Password Recovery</h1>
                </div>
                <p class="subtitle">Forgot Password</p>
            </div>

            <!-- Login Form -->
            <form @submit.prevent="handleSignup" class="login-form">
                <div class="form-group">
                    <label for="mobile">Email</label>
                    <div class="input-container">
                        <span class="input-icon">📧</span>
                        <input type="email" id="mobile" v-model="form.mobile" placeholder="Enter email" required />
                    </div>
                </div>

                <div class="form-group">
                    <p v-if="errmsg !== null" class="text-danger">{{ errmsg }}</p>
                </div>
                <button type="submit" class="login-btn" :disabled="loading">
                    <span v-if="loading" class="spinner"></span>
                    <span v-else>Submit</span>
                </button>
            </form>

            <!-- Footer -->
            <div class="login-footer">
                <p>Already have an account? <router-link to="/login" class="signup-link">Sign in</router-link></p>
            </div>
        </div>

        <!-- Background Elements -->
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
    </div>
     <!-- Mail Sent Success Message -->
    <div class="mail-sent-container" v-else>
        <div class="success-card">
            <!-- Success Icon -->
            <div class="success-icon">
                <div class="icon-circle">
                    <span class="mail-icon">📧</span>
                    <div class="checkmark"></div>
                </div>
            </div>

            <!-- Success Message -->
            <div class="success-message">
                <h1>Check Your Email</h1>
                <p class="subtitle">We've sent a password reset link to</p>
                <p class="user-email">{{ form.mobile }}</p>
                <p class="instructions">
                    Click the link in the email to reset your password. The link will expire in 24 hours.
                </p>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
                <button class="resend-btn" @click="handleResend" :disabled="resending">
                    <span v-if="resending" class="spinner"></span>
                    <span v-else>Resend Email</span>
                </button>
                <button class="back-btn" @click="handleBackToLogin">
                    Back to Login
                </button>
            </div>

            <!-- Help Text -->
            <div class="help-text">
                <p>Didn't receive the email? Check your spam folder or 
                   <a href="#" @click.prevent="handleResend" class="resend-link">click here to resend</a>
                </p>
            </div>
        </div>

        <!-- Background Elements -->
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
    </div>
</template>



<script setup>
import router from "@/router";
import authService from "@/services/auth.service";
import toastService from "@/services/toastService";
import { ref, reactive } from "vue";

const success = ref(false);
const resending = ref(false);
const form = reactive({
    mobile: "",
    password: "",
    rememberMe: false,
});

const loading = ref(false);
const errmsg = ref(null);

const handleSignup = async () => {
    errmsg.value = null;
    loading.value = true;
    
    try {
        // Note: You should update this to call your password reset endpoint
        // const resp = await authService.forgotPassword({ email: form.mobile });
        const resp = await authService.forgotPassword({ 
            email: form.mobile 
        });
        
        if (!resp.error) {
            success.value = true;
            toastService.success("Password reset link sent to your email!");
        } else {
            errmsg.value = resp.error ? resp.error.message : "Server is unreachable";
        }
    } catch (error) {
        errmsg.value = error.error ? error.error.message : "Server is unreachable";
        toastService.error(errmsg.value);
    } finally {
        loading.value = false;
    }
};

const handleResend = async () => {
    resending.value = true;
    try {
        // Call resend email API
        const resp = await authService.resendResetEmail({ email: form.mobile });
        if (!resp.error) {
            toastService.success("Password reset link sent again!");
        } else {
            toastService.error(resp.error.message || "Failed to resend email");
        }
    } catch (error) {
        toastService.error("Failed to resend email");
    } finally {
        resending.value = false;
    }
};

const handleBackToLogin = () => {
    router.push('/login');
};
</script>

<style scoped>
.login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ea6666 0%, #a26e4b 100%);
    padding: 20px;
    position: relative;
    overflow: hidden;
}

.login-card {
    background: white;
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 420px;
    position: relative;
    z-index: 2;
    backdrop-filter: blur(10px);
}

.login-header {
    text-align: center;
    margin-bottom: 30px;
}

.logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 8px;
}

.logo-icon {
    font-size: 2rem;
}

h1 {
    color: #2d3748;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
}

.subtitle {
    color: #718096;
    margin: 0;
    font-size: 0.95rem;
}

.login-form {
    margin-bottom: 25px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    color: #2d3748;
    font-weight: 500;
    font-size: 0.9rem;
}

.input-container {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 15px;
    font-size: 1.1rem;
    z-index: 1;
}

input {
    width: 100%;
    padding: 12px 45px 12px 45px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f8fafc;
}

input:focus {
    outline: none;
    border-color: #b6300f;
    background: white;
    box-shadow: 0 0 0 3px rgba(182, 48, 15, 0.1);
}

.password-toggle {
    position: absolute;
    right: 15px;
    background: none;
    border: none;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0;
}

.form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
}

.checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
    color: #4a5568;
}

.checkbox-container input {
    display: none;
}

.checkmark {
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    margin-right: 8px;
    position: relative;
    transition: all 0.3s ease;
}

.checkbox-container input:checked+.checkmark {
    background: #b6300f;
    border-color: #b6300f;
}

.checkbox-container input:checked+.checkmark::after {
    content: "✓";
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
}

.forgot-password {
    color: #b6300f;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
}

.forgot-password:hover {
    text-decoration: underline;
}

.login-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #b6300f, #d93814);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.login-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(182, 48, 15, 0.3);
}

.login-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 2px solid transparent;
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.divider {
    text-align: center;
    margin: 25px 0;
    position: relative;
}

.divider::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #e2e8f0;
}

.divider span {
    background: white;
    padding: 0 15px;
    color: #718096;
    font-size: 0.9rem;
}

.social-login {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 25px;
}

.social-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
}

.social-btn:hover {
    border-color: #b6300f;
    transform: translateY(-1px);
}

.login-footer {
    text-align: center;
}

.login-footer p {
    color: #718096;
    margin: 0;
    font-size: 0.9rem;
}

.signup-link {
    color: #b6300f;
    text-decoration: none;
    font-weight: 600;
}

.signup-link:hover {
    text-decoration: underline;
}

.floating-element {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(182, 48, 15, 0.1), rgba(106, 176, 76, 0.1));
    animation: float 6s ease-in-out infinite;
}

.element-1 {
    width: 100px;
    height: 100px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
}

.element-2 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    right: 10%;
    animation-delay: 2s;
}

.element-3 {
    width: 80px;
    height: 80px;
    top: 60%;
    left: 5%;
    animation-delay: 4s;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

@media (max-width: 480px) {
    .login-card {
        padding: 30px 20px;
    }

    .social-login {
        grid-template-columns: 1fr;
    }
}

.mail-sent-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ea6666 0%, #a26e4b 100%);
    padding: 20px;
    position: relative;
    overflow: hidden;
}

.success-card {
    background: white;
    padding: 50px 40px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 480px;
    position: relative;
    z-index: 2;
    text-align: center;
}

.success-icon {
    margin-bottom: 30px;
}

.icon-circle {
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, #4CAF50, #45a049);
    border-radius: 50%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    animation: scaleIn 0.5s ease-out;
}

.mail-icon {
    font-size: 3rem;
    color: white;
}

.checkmark {
    position: absolute;
    bottom: 5px;
    right: 5px;
    width: 30px;
    height: 30px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.checkmark::after {
    content: "✓";
    color: #4CAF50;
    font-size: 16px;
    font-weight: bold;
}

.success-message h1 {
    color: #2d3748;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.success-message .subtitle {
    color: #718096;
    margin-bottom: 5px;
    font-size: 1rem;
}

.user-email {
    color: #b6300f;
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 20px;
    padding: 8px 16px;
    background: #f8fafc;
    border-radius: 8px;
    display: inline-block;
}

.instructions {
    color: #4a5568;
    line-height: 1.6;
    margin: 20px 0;
    font-size: 0.95rem;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 30px 0;
}

.resend-btn, .back-btn {
    padding: 14px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
}

.resend-btn {
    background: linear-gradient(135deg, #b6300f, #d93814);
    color: white;
}

.resend-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(182, 48, 15, 0.3);
}

.resend-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.back-btn {
    background: #f8fafc;
    color: #4a5568;
    border: 2px solid #e2e8f0;
}

.back-btn:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}

.help-text {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.help-text p {
    color: #718096;
    font-size: 0.9rem;
    margin: 0;
}

.resend-link {
    color: #b6300f;
    text-decoration: none;
    font-weight: 500;
}

.resend-link:hover {
    text-decoration: underline;
}

/* Animation for icon */
@keyframes scaleIn {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    70% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Reuse existing floating elements animation */
.floating-element {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(182, 48, 15, 0.1), rgba(106, 176, 76, 0.1));
    animation: float 6s ease-in-out infinite;
}

.element-1 {
    width: 100px;
    height: 100px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
}

.element-2 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    right: 10%;
    animation-delay: 2s;
}

.element-3 {
    width: 80px;
    height: 80px;
    top: 60%;
    left: 5%;
    animation-delay: 4s;
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
    }
    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

@media (max-width: 480px) {
    .success-card {
        padding: 40px 20px;
    }
    
    .icon-circle {
        width: 80px;
        height: 80px;
    }
    
    .mail-icon {
        font-size: 2.5rem;
    }
    
    .success-message h1 {
        font-size: 1.6rem;
    }
}
</style>